import { initContract } from "@ts-rest/core";
import { z } from "zod";

import {
  applicationMembersSchema,
  applicationSchema,
  applicationStatusEnumSchema,
  applicationTypeEnumSchema,
  insertApplicationSchema,
  updateApplicationSchema,
} from "@libs/db-schemas";

import { HTTPStatusCode } from "../../types";
import { errorSchema } from "../../utils/errors";
import {
  listApplicationsResponseSchema,
  recentDuplicateApplicationQuerySchema,
  recentDuplicateApplicationsSchema,
} from "./schemas";

const c = initContract();

export const applicationsContract = c.router({
  createApplication: {
    summary:
      "Create an application for the current patient and given household members",
    method: "POST",
    path: "/applications",
    responses: {
      [HTTPStatusCode.CREATED]: z.object({
        application: applicationSchema,
        applicationMembers: z.array(applicationMembersSchema),
      }),
      [HTTPStatusCode.BAD_REQUEST]: errorSchema,
      [HTTPStatusCode.NOT_FOUND]: errorSchema,
      [HTTPStatusCode.UNAUTHORIZED]: errorSchema,
    },
    body: insertApplicationSchema
      .omit({
        patientId: true,
        id: true,
        applicationState: true,
      })
      .extend({
        householdMembers: z
          .array(
            z.object({
              memberId: z.uuid(),
              eligibilityCategoryId: z.uuid().optional(),
            }),
          )
          .min(1, {
            error:
              "Household members are required when creating an application",
          }),
        applicationType: applicationTypeEnumSchema,
      }),
  },
  listApplications: {
    summary: "Get a list of patient's applications",
    method: "GET",
    path: "/applications",
    query: z.object({
      status: applicationStatusEnumSchema.optional(),
      applicationType: applicationTypeEnumSchema.optional(),
    }),
    responses: {
      [HTTPStatusCode.OK]: listApplicationsResponseSchema,
    },
  },
  getApplicationsCount: {
    summary: "Get the count of applications for the current patient",
    method: "GET",
    path: "/applications/count",
    query: z.object({
      status: applicationStatusEnumSchema.optional(),
      applicationType: applicationTypeEnumSchema.optional(),
      ids: z.array(z.uuid()).optional(),
    }),
    responses: {
      [HTTPStatusCode.OK]: z.number(),
    },
  },
  listRecentDuplicateApplications: {
    summary:
      "List recently submitted applications that overlap selected household members",
    method: "GET",
    path: "/applications/recent-duplicates",
    query: recentDuplicateApplicationQuerySchema,
    responses: {
      [HTTPStatusCode.OK]: recentDuplicateApplicationsSchema,
    },
  },
  getApplication: {
    summary: "Get a specific application by ID for the current patient",
    method: "GET",
    path: "/applications/:applicationId",
    pathParams: z.object({
      applicationId: z.uuid(),
    }),
    responses: {
      [HTTPStatusCode.OK]: applicationSchema,
      [HTTPStatusCode.NOT_FOUND]: errorSchema,
    },
  },
  updateApplication: {
    summary: "Update an existing application for the current patient",
    method: "PATCH",
    path: "/applications/:id",
    pathParams: z.object({
      id: z.uuid(),
    }),
    body: updateApplicationSchema,
    responses: {
      [HTTPStatusCode.OK]: applicationSchema,
      [HTTPStatusCode.NOT_FOUND]: errorSchema,
    },
  },
  deleteDraftApplications: {
    summary: "Delete all draft applications for the current patient",
    method: "DELETE",
    path: "/applications/drafts",
    responses: {
      [HTTPStatusCode.OK]: c.noBody(),
    },
  },
});
